import { action, computed, observable, runInAction } from 'mobx';
// import React from "react";

import { Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { makeTableQuery, TableQuery } from '../../adl-gen/common/tabular';
import * as DB from '../../adl-gen/totalinteraction/futurefit/db';
// import { TranslateFn } from "../../internationalization/i18n-hoc";
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';

// import { UserGroupChange, UserQuizChange } from "./index";

export type DataType =
  | DB.AppClient
  | DB.AppClientLicence
  | DB.AppUser
  | DB.Competency
  | DB.CompetencyTrainingOffering
  | DB.Licence
  | DB.Skill
  | DB.Question
  | DB.Quiz
  | DB.SkillTrainingOffering
  | DB.TrainingOffering
  | DB.LearningProvider
  | DB.LearningProviderCatalogEntry
  | DB.UserGroup
  | DB.UserUploadRecord
  | DB.WorkRole
  | DB.Auth0SamlConnection;

/**
 * Abstraction from Service resolver methods. Add option to control
 * client updates - ie. do them immediately or independently.
 */
// tslint:disable-next-line: interface-over-type-literal
export type TransportLayer<T extends DataType> = {
  kind: string;
  create(item: T): Promise<DbKey<T>>;
  update(itemWithId: WithDbId<T>): Promise<void>;
  doDelete(id: DbKey<T>): Promise<void>;
  query(q: TableQuery): Promise<Paginated<WithDbId<T>>>;
};

export class DataStore<T extends DataType> {
  @observable.ref data?: Promise<Paginated<WithDbId<T>>>;
  @observable selection?: Promise<WithDbId<T>>;
  queryAll: TableQuery = makeTableQuery({});

  constructor(
    protected readonly transport: TransportLayer<T>,
    protected readonly identity: LoggedIn | LoggedInSuper,
    protected readonly id?: DbKey<T>
  ) {
  }

  queryId = (id: DbKey<T>, idField?: string): TableQuery =>
    makeTableQuery({
      filter: {
        kind: 'equals',
        value: {
          field: idField ? idField : 'id',
          value: id
        }
      }
    });

  @action
  public setSelected = async (id: DbKey<T>): Promise<WithDbId<T>> => {
    if (this.authorized) {
      return this.select(id);
      // this.selection = this.select(id);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly select = async (id: DbKey<T>): Promise<WithDbId<T>> => {
    const results = await this.query(this.queryId(id));
    if (results.total_size !== 1) {
      return Promise.reject(`${this.transport.kind} with id ${id} not found/invalid query`);
    }
    return results.items[0];
  };

  // deferRefresh set to TRUE in appclient-form and competency-form and unused elsewhere
  public create = async (item: T, deferRefresh: boolean = false): Promise<DbKey<T>> => {
    const id = this.transport.create(item);
    // TODO: OBSOLETE
    if (!deferRefresh) {
      runInAction(() => {
        this.reload();
      });
    }
    return id;
  };

  // deferRefresh set to TRUE in appclient-form and competency-form and unused elsewhere
  public update = async (itemWithDbId: WithDbId<T>, deferRefresh: boolean = false): Promise<void> => {
    await this.transport.update(itemWithDbId);
    // TODO: OBSOLETE
    if (!deferRefresh) {
      runInAction(() => this.reload());
    }
  };

  doDelete = async (id: DbKey<T>): Promise<void> => {
    await this.transport.doDelete(id);
  };

  /** @deprecated - for now */
  public _doDeletes = async (a: DbKey<T>[], deferRefresh: boolean = false): Promise<void> => {
    for (const id of a) {
      await this.doDelete(id);
    }
    // TODO: OBSOLETE
    if (!deferRefresh) {
      runInAction(() => this.reload());
    }
  };

  public query = async (q: TableQuery): Promise<Paginated<WithDbId<T>>> => {
    // --- [ FF2-457 bug ] ---------------------------------
    // TableQuery does not work well with I18nContent fields
    // q.sorting = this.overrideI18nSort(q);
    const result = await this.transport.query(q);
    // this.reapplyI18nSort(result);
    return result;
  };

  /** @deprecated This duplicates too much loading - try to move away from 'selected' id */
  @computed
  public get selected(): MaybeLoading<WithDbId<T>> | undefined {
    if (this.authorized) {
      if (this.selection) {
        return fromPromise(this.selection);
      } else {
        return undefined;
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  /// Check that, for admin, the user's AppClientId matches that of the Site id.
  /// These can all be defeated by client-side attacks but will be enforced on the server anyway.
  get authorized(): boolean {
    return (
      this.isSuperUser ||
      (this.identity.kind === 'logged-in' &&
        this.identity.role === DB.ApplicationRole.admin &&
        this.identity.appUserWithDbId.value.appClientId === this.identity.appClient.id)
    );
  }

  /// Just check for super user
  get isSuperUser(): boolean {
    return this.identity.kind === 'super-logged-in';
  }

  /**
   * override get - in this case, we are not just checking that the Site id
   * matches the user's AppClientId, but also that the site ID matches the AppClientId used
   * in API fetches.
   */
  authorizedForClient = (appClientId: DbKey<DB.AppClient>): boolean => {
    return (
      this.authorized &&
      (this.identity.kind === 'super-logged-in' ||
        (this.identity.kind === 'logged-in' && this.identity.appClient.id === appClientId))
    );
  };

  init = (id?: DbKey<T>): Promise<Paginated<WithDbId<T>>> => {
    if (this.authorized) {
      // if (id) { this.selection = this.select(id); }
      return this.load(id);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  @computed
  get loading(): MaybeLoading<Paginated<WithDbId<T>>> {
    if (this.authorized) {
      if (this.data) {
        return fromPromise(this.data);
      } else {
        return { kind: 'loading' };
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  load = async (id?: DbKey<T>): Promise<Paginated<WithDbId<T>>> => {
    if (id) {
      this.selection = this.setSelected(id);
    }
    const selection = await this.selection;
    const results = await this.query(selection ? this.queryId(selection.id) : this.queryAll);
    return results;
  };

  @action
  reload = async () => {
    let selection;
    if (this.selection) {
      selection = await this.selection;
    }
    this.data = this.load(selection ? selection.id : undefined);
  };
}



// WEBPACK FOOTER //
// ./src/admin/stores/datastore.ts